import React, {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {type AppDispatch, type RootState} from 'redux/store'
import {TextField, Typography} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import ReactCountryFlag from 'react-country-flag'
import FormikSelect from 'shared/FormikSelect'
import {fetchAllPhoneCodeList, type IPhoneCountryCode} from 'redux/common.slice'
import _ from 'lodash'
import './index.scss'
import {useLocation} from 'react-router-dom'
import {ENDPOINT} from 'config/path'

interface FormikPhoneProps {
    id: string
    flagId: string
    formik: any
    defaultCode?: any
    maxLength?: number
    popup?: boolean
    disabled?: boolean
    customMenuClassName?: string
    customClassName?: string
}

const FormikPhone = ({
    flagId,
    id,
    formik,
    defaultCode,
    maxLength,
    popup,
    customMenuClassName,
    customClassName,
    disabled
}: FormikPhoneProps) => {
    const location = useLocation()
    const dispatch = useDispatch<AppDispatch>()
    const [focus, setFocus] = useState<boolean>(false)
    const [dropdownFocus, setDropdownFocus] = useState<string>('')
    const [className, setClassName] = useState<string>('')

    const options = useSelector((state: RootState) => state.common.allPhoneCodes)

    let countries: any[] = []
    if (options && typeof options === 'object' && options.length > 0) {
        countries = options.map((option) => ({
            phone: Number(option.countryPhone),
            code: option.countryCode,
            country: option.country
        }))
    }
    countries = countries.filter((item, index) => {
        const currentIndex = countries.findIndex((obj) => obj.code === item.code)
        return currentIndex === index
    })
    const usa: any = countries.filter((item) => item.country === 'United States')
    countries = countries.filter((item) => item.country !== 'United States')
    countries = countries.sort((a, b) => a.country.localeCompare(b.country))
    const flagList = countries.map((option) => ({
        value: String(option.phone) + ':' + String(option.country),
        label: (
            <div className='formikphone__list-item'>
                <ReactCountryFlag alt={option.code} countryCode={option.code} svg />
                <Typography variant='h6'>
                    &emsp;{option.country}&nbsp;+{option.phone}
                </Typography>
            </div>
        )
    }))

    if (usa.length > 0) {
        const usaIndex = {
            value: String(usa[0].phone) + ':' + String(usa[0].country),
            label: (
                <div className='formikphone__list-item'>
                    <ReactCountryFlag alt='US' countryCode={usa[0].code} svg />
                    <Typography variant='h6'>
                        &emsp;{usa[0].country}&nbsp;+{usa[0].phone}
                    </Typography>
                </div>
            )
        }
        flagList.unshift(usaIndex)
    }
    const formatUSPhone = (value) => {
        let cleanedInput = value.replace(/\D/g, '')
        let formattedNumber = ''
        if (cleanedInput.length > 10) {
            cleanedInput = cleanedInput.substring(0, 10)
        }
        if (cleanedInput.length > 6) {
            formattedNumber = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3, 6)}-${cleanedInput.slice(6)}`
        } else if (cleanedInput.length > 3) {
            formattedNumber = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3)}`
        } else if (cleanedInput.length > 0) {
            formattedNumber = `(${cleanedInput.slice(0, 3)}`
        }
        return formattedNumber
    }

    const changeHandler = (event) => {
        if (_.get(formik.values, flagId, '') === '1:United States') {
            formik.setFieldValue(id, formatUSPhone(event.target.value))
        } else {
            formik.setFieldValue(id, event.target.value.replace(/\D/g, ''))
        }
    }

    const blurHandler = () => {
        formik.setFieldTouched(id)
        setFocus(false)
    }

    const selectChangeHandler = (event) => {
        const phone = _.get(formik.values, id, '')
        if (event.target.value === '1:United States') {
            formik.setFieldValue(id, formatUSPhone(phone))
        } else {
            const cleanedInput = phone.replace(/\D/g, '')
            formik.setFieldValue(id, cleanedInput)
        }
    }

    useEffect(() => {
        const phone = _.get(formik.values, id)
        if (phone?.trim()?.length > 0 && !location.pathname.includes(ENDPOINT.branchUserRegister)) {
            if (_.get(formik.values, flagId) === '1:United States') {
                const formattedPhoneValue = formatUSPhone(phone)
                if (formattedPhoneValue !== phone) {
                    void formik.setFieldValue(id, formattedPhoneValue)
                }
            } else {
                const cleanedInput = phone.replace(/\D/g, '')
                if (cleanedInput !== phone) {
                    void formik.setFieldValue(id, cleanedInput)
                }
            }
        }
    }, [_.get(formik.values, flagId), formik])

    useEffect(() => {
        let className = 'formikPhone-container'
        if (focus) {
            className = 'formikPhone-container focus'
        }
        if (_.get(formik.touched, id) && Boolean(_.get(formik.errors, id))) {
            className = 'formikPhone-container border-error'
            if (focus) {
                className = 'formikPhone-container border-error error-focus'
            }
        }
        setClassName(className)
    }, [focus, formik.touched, formik.errors, id])

    useEffect(() => {
        if (options?.length > 0) {
            if (defaultCode?.trim().length > 0) {
                const defaultValue: any = options.filter((item: IPhoneCountryCode) => item.countryCode === defaultCode)
                if (defaultValue?.length > 0) {
                    formik.setFieldValue(
                        flagId,
                        String(defaultValue[0].countryPhone) + ':' + String(defaultValue[0].country)
                    )
                }
            }
        } else {
            void dispatch(fetchAllPhoneCodeList())
        }
    }, [])

    return (
        <div>
            <div className={className}>
                <FormikSelect
                    id={flagId}
                    popup={popup}
                    formik={formik}
                    placeholder=''
                    options={flagList}
                    className={`flag-phone ${dropdownFocus} ${customClassName ?? ''}`}
                    onChange={selectChangeHandler}
                    focusHandler={() => {
                        setDropdownFocus('flag-focus')
                        setFocus(true)
                    }}
                    blurHandler={() => {
                        setDropdownFocus('')
                        setFocus(false)
                    }}
                    MenuClassName={customMenuClassName !== undefined ? customMenuClassName : 'dropdown-list'}
                />
                <TextField
                    id={id}
                    name={id}
                    value={_.get(formik.values, id, '')}
                    onChange={changeHandler}
                    className='formikPhone__text'
                    onBlur={blurHandler}
                    InputProps={{
                        inputProps: {
                            maxLength
                        }
                    }}
                    onFocus={() => setFocus(true)}
                    autoComplete='on'
                    disabled={disabled}
                />
            </div>
            {_.get(formik.touched, id) && Boolean(_.get(formik.errors, id)) && (
                <p className='formik___error-text'>
                    <ErrorOutlineOutlinedIcon className='formik-error__info-icon' />
                    {_.get(formik.errors, id)}
                </p>
            )}
        </div>
    )
}

FormikPhone.defaultProps = {
    maxLength: 25,
    popup: false,
    customClassName: ''
}

export default FormikPhone
